import React, { useLayoutEffect, useState } from "react";
import {
  Link as RouterLink,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import {
  Box,
  GlobalStyles,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  alpha,
  SxProps,
  Theme,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { ChevronRightRounded } from "@mui/icons-material";
import { HomeIcon } from "../../icons/customIcons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { ClientPortalStatus } from "../../store/apis/clientPortalApi";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import {
  ChartMode,
  CLIENT_SIDEBAR_WIDTH,
  COLLAPSED_SIDEBAR_WIDTH,
  isUnknownPlatform,
  platformIcon,
  platformLabel,
  statusColor,
} from "./clientFormatViewClient";
import {
  PAGE_TARGET_ATTR,
  PAGE_TRANSITION,
  TITLE_TRANSITION,
  TRANSITION_DIRECTION_ATTR,
  transitionLinkProps,
  useClientTransition,
} from "./clientTransitionViewClient";

// Piezas de la vista de cliente. Diseño con mucho aire: pocas cosas por
// pantalla, cifras grandes y textos cortos.

const PAGE_PX = { xs: 2.5, md: 5 };
/** Relleno lateral de las tarjetas: las tablas lo usan para llegar al borde. */
export const CARD_PX = { xs: 2.5, md: 3.5 };

// Ritmo de las transiciones entre páginas: hacia dentro, lo pulsado se abre
// hasta ser la página (la nueva entra recortada desde arriba, lo pulsado se
// queda en su sitio mientras se desvanece); hacia fuera, al revés, la página se
// cierra recortada en la fila. El nombre viaja con el título.
const EASE = "cubic-bezier(0.2, 0, 0, 1)";
const grows = {
  height: "100%",
  objectFit: "cover",
  objectPosition: "top",
} as const;
const stays = {
  height: "100%",
  objectFit: "none",
  objectPosition: "left top",
} as const;
const back = `html[${TRANSITION_DIRECTION_ATTR}="back"]`;
const transitionGlobals = {
  [`::view-transition-group(${PAGE_TRANSITION}), ::view-transition-group(${TITLE_TRANSITION})`]:
    { animationDuration: "450ms", animationTimingFunction: EASE },
  [`::view-transition-new(${PAGE_TRANSITION})`]: grows,
  [`::view-transition-old(${PAGE_TRANSITION})`]: stays,
  [`${back}::view-transition-old(${PAGE_TRANSITION})`]: grows,
  [`${back}::view-transition-new(${PAGE_TRANSITION})`]: stays,
} as const;

/**
 * Página de cliente: una columna centrada y, si se le pasa, una banda de lado
 * a lado debajo (sin márgenes) que llega hasta el final de la pantalla. Con
 * `wide`, la columna llega hasta los márgenes laterales, como la banda.
 */
export const ClientPage: React.FC<{
  children: React.ReactNode;
  band?: React.ReactNode;
  wide?: boolean;
}> = ({ children, band, wide }) => {
  const theme = useTheme();
  // El inicio lo ven todos los roles: con el sidebar general, que se pliega,
  // se deja el margen de su ancho. El de cliente nunca está plegado.
  const collapsed = useSelector(selectSidebarCollapsed);
  // Cada página empieza arriba del todo, aunque la anterior estuviera bajada.
  // Antes de pintarse (también dentro de una transición). Con atrás/adelante
  // del navegador no: ahí manda su propia vuelta a la posición.
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useLayoutEffect(() => {
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [pathname, navigationType]);
  return (
    <Box
      component="main"
      sx={{
        ml: `${collapsed ? COLLAPSED_SIDEBAR_WIDTH : CLIENT_SIDEBAR_WIDTH}px`,
        transition: "margin-left 0.2s ease",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        pt: { xs: 3, md: 4 },
        pb: band ? 0 : { xs: 3, md: 4.5 },
        boxSizing: "border-box",
        "@media print": { ml: 0 },
      }}
    >
      <GlobalStyles styles={transitionGlobals} />
      <Box sx={{ px: PAGE_PX }}>
        <Box
          {...(wide ? {} : { [PAGE_TARGET_ATTR]: "" })}
          sx={{
            maxWidth: wide ? "none" : 1120,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3.5,
            // Lo pulsado se abre hasta ser este contenido. El inicio (`wide`) no
            // lo lleva: allí el nombre es de la fila pulsada.
            ...(wide ? {} : { viewTransitionName: PAGE_TRANSITION }),
          }}
        >
          {children}
        </Box>
      </Box>
      {band}
    </Box>
  );
};

/** Banda de lado a lado para el final de una página (p. ej. la tabla). */
export const ClientBand: React.FC<{
  title: React.ReactNode;
  /** Icono delante del título. */
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => {
  const theme = useTheme();
  return (
    <Box
      component="section"
      sx={{
        mt: 3.5,
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        px: PAGE_PX,
        pt: { xs: 3, md: 3.5 },
        pb: { xs: 3, md: 4.5 },
      }}
    >
      <Typography
        component="h2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.3,
          mb: 2.5,
        }}
      >
        {icon}
        {title}
      </Typography>
      {children}
    </Box>
  );
};

export const ClientCard: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  /** Sin tarjeta: el contenido flota sobre el fondo de la página. */
  floating?: boolean;
}> = ({ title, subtitle, action, children, sx, floating }) => {
  const theme = useTheme();
  return (
    <Box
      component="section"
      sx={{
        ...(floating
          ? {}
          : {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "20px",
              px: CARD_PX,
              py: { xs: 2.5, md: 3 },
            }),
        minWidth: 0,
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography
                component="h2"
                sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
};

// La última elección se recuerda al cambiar de pantalla, hasta recargar.
let lastChartMode: ChartMode = "cumulative";

export const useChartMode = (): [ChartMode, (mode: ChartMode) => void] => {
  const [mode, setMode] = useState<ChartMode>(lastChartMode);
  const change = (next: ChartMode) => {
    lastChartMode = next;
    setMode(next);
  };
  return [mode, change];
};

/** Switch Diario / Acumulado de las gráficas de avance. */
export const ChartModeSwitch: React.FC<{
  value: ChartMode;
  onChange: (mode: ChartMode) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <PillToggle
      value={value}
      onChange={onChange}
      label={t("clientView.charts.modeLabel")}
      options={[
        { value: "daily", label: t("clientView.charts.modeDaily") },
        { value: "cumulative", label: t("clientView.charts.modeCumulative") },
      ]}
    />
  );
};

/** Grupo de pastillas para elegir una opción (vista, métrica...). */
export const PillToggle = <T extends string>({
  value,
  onChange,
  label,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  label: string;
  options: { value: T; label: React.ReactNode }[];
}) => {
  const theme = useTheme();
  const buttonSx = {
    px: 1.5,
    py: 0.375,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    border: 0,
    borderRadius: "999px !important",
    color: "text.secondary",
    "&.Mui-selected, &.Mui-selected:hover": {
      color: "text.primary",
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
    },
  } as const;
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, next: T | null) => next && onChange(next)}
      aria-label={label}
      sx={{
        p: 0.5,
        gap: 0.5,
        borderRadius: 999,
        backgroundColor: theme.palette.neutral[200],
      }}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value} sx={buttonSx}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export const StatTile: React.FC<{
  label: string;
  value: string;
  hint?: string;
  /** Gráfica pequeña a la derecha de la cifra. */
  chart?: React.ReactNode;
  /** Sin tarjeta: la cifra flota sobre el fondo de la página. */
  floating?: boolean;
}> = ({ label, value, hint, chart, floating }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...(floating
          ? {}
          : {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "20px",
              px: 3,
              py: 2.5,
            }),
        minWidth: 0,
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <Box sx={{ minWidth: 0, position: "relative", zIndex: 1 }}>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.75 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 24, md: 28 },
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75 }}>
            {hint}
          </Typography>
        )}
      </Box>
      {/* Pegada a la cifra y un poco por debajo de ella, como un fondo. */}
      {chart && <Box sx={{ ml: -1.5, flexShrink: 0 }}>{chart}</Box>}
    </Box>
  );
};

export const StatGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
        // Solo cuentan las cifras que se pintan, y como mucho 4 por fila.
        lg: `repeat(${Math.min(
          React.Children.toArray(children).length,
          4
        )}, minmax(0, 1fr))`,
      },
      gap: 2,
    }}
  >
    {children}
  </Box>
);

/** Marca del estado: triángulo "play" si está activa, cuadrado "stop" si ha
 * terminado y punto si aún no ha empezado. La forma distingue sin el color. */
export const StatusMark: React.FC<{
  status: ClientPortalStatus;
  size?: number;
}> = ({ status, size = 8 }) => {
  const color = statusColor(status);
  if (status === "LIVE")
    return (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 0,
          height: 0,
          flexShrink: 0,
          borderTop: `${size / 2 + 1}px solid transparent`,
          borderBottom: `${size / 2 + 1}px solid transparent`,
          borderLeft: `${size + 1}px solid ${color}`,
        }}
      />
    );
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: status === "FINISHED" ? "1px" : "50%",
        bgcolor: color,
      }}
    />
  );
};

/** Estado con marca de color y texto: el color nunca va solo. */
export const StatusPill: React.FC<{ status: ClientPortalStatus }> = ({
  status,
}) => {
  const { t } = useTranslation();
  const color = statusColor(status);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.375,
        borderRadius: 999,
        backgroundColor: alpha(color, 0.12),
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: "text.primary",
      }}
    >
      <StatusMark status={status} />
      {t(`clientView.status.${status}`)}
    </Box>
  );
};

export const PlatformTag: React.FC<{ platform: string }> = ({ platform }) => {
  if (isUnknownPlatform(platform))
    return (
      <Box component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
        {platformLabel(platform)}
      </Box>
    );
  const icon = platformIcon(platform);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        fontSize: 13,
      }}
    >
      <Box
        component="img"
        src={icon}
        alt=""
        sx={{
          width: 20,
          height: 20,
          borderRadius: "6px",
          flexShrink: 0,
          // Sin pastilla: los iconos ya están pensados también para el modo
          // oscuro.
        }}
      />
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {platformLabel(platform)}
      </Box>
    </Box>
  );
};

/** Barra de avance hacia el objetivo; pasa del 100 % sin desbordar. */
// Azul eléctrico de las barras destacadas (el mismo que el de las impresiones).
export const ELECTRIC_BLUE = { light: "#1f5bff", dark: "#4d7dff" } as const;
const flow = keyframes`
  from { background-position: 0% 50%; }
  to { background-position: 200% 50%; }
`;

/**
 * Barra de avance. Con `glow` (la del objetivo de una subcampaña) es algo más
 * gruesa y lleva un degradado que fluye despacio hacia un azul más eléctrico,
 * con un brillo suave: que se note viva, sin dejar de ser seria.
 */
export const ProgressBar: React.FC<{
  value: number;
  color?: string;
  glow?: boolean;
  /** Raya vertical en este punto (0–1) con su etiqueta encima: p. ej. por
   * dónde se debería ir hoy. */
  marker?: { value: number; label: string };
}> = ({ value, color, glow, marker }) => {
  const theme = useTheme();
  const base = color ?? theme.palette.info.main;
  const electric = ELECTRIC_BLUE[theme.palette.mode];
  const markerAt = marker ? Math.min(1, Math.max(0, marker.value)) * 100 : 0;
  return (
    <Box
      sx={{
        position: "relative",
        height: glow ? 14 : 10,
        borderRadius: 999,
        backgroundColor: theme.palette.neutral[200],
        // Sin recortar: el brillo y la raya asoman un poco por fuera.
        overflow: glow || marker ? "visible" : "hidden",
        ...(marker && { mt: 3 }),
      }}
    >
      {marker && (
        <>
          <Typography
            component="span"
            sx={{
              position: "absolute",
              bottom: "calc(100% + 6px)",
              left: `${markerAt}%`,
              // Pegada a la raya, sin salirse por los bordes.
              transform: `translateX(${markerAt > 90 ? "-100%" : markerAt < 10 ? "0" : "-50%"})`,
              fontSize: 12,
              fontWeight: 600,
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            {marker.label}
          </Typography>
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: -4,
              bottom: -4,
              left: `${markerAt}%`,
              width: 2,
              ml: "-1px",
              borderRadius: 1,
              backgroundColor: theme.palette.text.primary,
              opacity: 0.7,
              zIndex: 1,
            }}
          />
        </>
      )}
      <Box
        sx={{
          height: "100%",
          width: `${Math.min(1, Math.max(0, value)) * 100}%`,
          borderRadius: 999,
          backgroundColor: base,
          transition: "width 0.6s ease",
          ...(glow && {
            backgroundImage: `linear-gradient(90deg, ${base}, ${electric}, ${base})`,
            backgroundSize: "200% 100%",
            animation: `${flow} 4s linear infinite`,
            boxShadow: `0 0 12px ${alpha(electric, 0.35)}, 0 0 2px ${alpha(electric, 0.5)}`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }),
        }}
      />
    </Box>
  );
};

/**
 * Ruta de vuelta: Inicio › Campaña › Subcampaña. Con `returnTo`, la página se
 * cierra con una transición en esa fila de la página a la que se vuelve.
 */
export const ClientTrail: React.FC<{
  items: { label: string; to?: string; returnTo?: string }[];
}> = ({ items }) => {
  const go = useClientTransition();
  return (
    <Box
      component="nav"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.5,
        fontSize: 13,
        color: "text.secondary",
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={`${item.label}-${index}`}>
          {index > 0 && <ChevronRightRounded sx={{ fontSize: 18 }} />}
          {item.to ? (
            <Box
              component={RouterLink}
              to={item.to}
              {...transitionLinkProps(go, item.to, { returnTo: item.returnTo })}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                color: "inherit",
                textDecoration: "none",
                // Como las migas del resto de la app: fondo al pasar por
                // encima; el margen negativo compensa el relleno.
                px: 0.75,
                py: 0.25,
                mx: -0.75,
                my: -0.25,
                borderRadius: 1.5,
                border: "1px solid transparent",
                transition: (theme) =>
                  theme.transitions.create(
                    ["background-color", "border-color", "color"],
                    { duration: theme.transitions.duration.shortest }
                  ),
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "action.hover",
                  borderColor: "divider",
                },
              }}
            >
              {/* El enlace al inicio lleva la casita del sidebar general. */}
              {item.to === "/" && <HomeIcon color="currentColor" size={18} />}
              {item.label}
            </Box>
          ) : (
            <Box
              component="span"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {item.label}
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

/** Cabecera de página: título grande y, debajo, una línea de detalles. */
export const ClientHeader: React.FC<{
  title?: string;
  children?: React.ReactNode;
}> = ({ title, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    {title && (
      <Typography
        component="h1"
        {...{ [PAGE_TARGET_ATTR]: "" }}
        sx={{
          // El nombre de lo pulsado viaja hasta aquí; del ancho del texto para
          // que no se deforme por el camino.
          viewTransitionName: TITLE_TRANSITION,
          width: "fit-content",
          maxWidth: "100%",
          fontSize: { xs: 24, md: 30 },
          fontWeight: 700,
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          overflowWrap: "anywhere",
        }}
      >
        {title}
      </Typography>
    )}
    {children && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 2.5,
          rowGap: 1,
          color: "text.secondary",
          fontSize: 13,
        }}
      >
        {children}
      </Box>
    )}
  </Box>
);
